import * as ngCore from '@angular/core'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import { Router } from '../router'

export class EncodedQueryParamsStorage<
  // NOTE: this must NOT have any default! If it does, it won't get inferred,
  // which breaks a lot
  const TPropertiesConfig extends store.storage.PropertiesConfig.Constraint,
> extends store.storage.Feature<TPropertiesConfig> {
  /**
   * Default name for a {@link EncodedQueryParamsStorage}. If you have more than one
   * {@link EncodedQueryParamsStorage} using the same
   * {@link EncodedQueryParamsStorage.constructor.Params.parent}, you should pass
   * {@link EncodedQueryParamsStorage.constructor.Params.name} to avoid collisions.
   */
  public static readonly DEFAULT_NAME = 'EncodedQueryParamsStorage'

  /** @see {@link Router} */
  private readonly _router: Router

  constructor(
    params: Readonly<QueryParamsStorage.constructor.Params<TPropertiesConfig>>,
  ) {
    super({
      ...params,
      initialValues: rxjs.defer(() =>
        this._router.state.asObservable
          // Wait until the initial router state is set up
          .pipe(
            rxjs.filter(state => state !== undefined),
            // Then we get the current query params at that point
            rxjs.exhaustMap(
              // NOTE: query params can be any type, but we hope what is actually in
              // the query params corresponds to the declared storage properties in
              // this feature
              () =>
                this._router.selectors.getEncodedQueryParam(this.lineage)
                  .asObservable satisfies rxjs.Observable<unknown> as rxjs.Observable<
                  store.storage.State.Values<TPropertiesConfig>
                >,
            ),
            // We only need the first value
            rxjs.take(1),
          ),
      ),
      name: params.name ?? EncodedQueryParamsStorage.DEFAULT_NAME,
    })
    this._router = params.router ?? ngCore.inject(Router)
  }

  /**
   * Sends a single query param for this {@link store.storage.Feature} to the
   * {@link Router}
   */
  protected onWriteProperty: store.storage.Feature<TPropertiesConfig>['onWriteProperty'] =
    (forwardTo, { entry }) =>
      rxjs.of(
        forwardTo(this._router.actions.navigate, {
          input: {
            queryParams: {
              encoded: [
                {
                  // This just writes the single query param for this key
                  scope: [...this.lineage, entry.key],
                  // With the value for this key
                  value: entry.value,
                },
              ],
            },
          },
        }),
      )

  /**
   * Sends all the query params for this {@link store.storage.Feature} to the
   * {@link Router}
   */
  protected onWriteAllProperties: store.storage.Feature<TPropertiesConfig>['onWriteAllProperties'] =
    (forwardTo, { values }) =>
      rxjs.of(
        forwardTo(this._router.actions.navigate, {
          input: {
            queryParams: {
              encoded: [
                {
                  // This overwrites all the query params
                  scope: this.lineage,
                  // With the full values
                  value: values,
                },
              ],
            },
          },
        }),
      )
}
export namespace QueryParamsStorage {
  export namespace constructor {
    export interface Params<
      TPropertiesConfig extends store.storage.PropertiesConfig.Constraint,
    > extends Partial.Semi<
        Omit<
          store.storage.Feature.constructor.Params<TPropertiesConfig>,
          'initialValues'
        >,
        'name'
      > {
      /**
       * {@link Router} is injected by default. However, if
       * {@link EncodedQueryParamsStorage} is instantiated in a non-injectable context,
       * the {@link Router} must be passed here
       */
      router?: Router
    }
  }
}
